import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useCustomToast,
} from '@elkaso-app/web-design';
import { ICrmMasterCuisineData, useUpdateCrmMasterCuisineApi } from 'apis/crm-master-cuisines';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface EditMasterCuisineModalProps {
  isOpen: boolean;
  onClose: () => void;
  masterCuisine: ICrmMasterCuisineData;
}

interface FormData {
  name: string;
  region?: string;
  country?: string;
}

export const EditMasterCuisineModal: React.FC<EditMasterCuisineModalProps> = ({ isOpen, onClose, masterCuisine }) => {
  const toast = useCustomToast();

  const { mutate: updateMasterCuisine, isLoading: isUpdating } = useUpdateCrmMasterCuisineApi({
    onSuccess: () => {
      toast({
        title: 'Success!',
        description: `Master cuisine "${masterCuisine.name}" has been updated successfully.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update master cuisine. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
  });

  // Reset form when modal opens or masterCuisine changes
  useEffect(() => {
    if (isOpen && masterCuisine) {
      reset({
        name: masterCuisine.name || '',
        region: masterCuisine.region || '',
        country: masterCuisine.country || '',
      });
    }
  }, [isOpen, masterCuisine, reset]);

  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        id: masterCuisine.id,
        name: data.name.trim(),
        region: data.region?.trim() || undefined,
        country: data.country?.trim() || undefined,
      };
      updateMasterCuisine(payload);
    },
    [updateMasterCuisine, masterCuisine.id]
  );

  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Master Cuisine</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isInvalid={!!errors.name} isRequired>
              <FormLabel>Cuisine Name</FormLabel>
              <Input
                placeholder='Enter cuisine name'
                {...register('name', {
                  required: 'Cuisine name is required',
                  maxLength: {
                    value: 255,
                    message: 'Cuisine name must be 255 characters or less',
                  },
                  validate: (value) => {
                    const trimmed = value.trim();
                    return trimmed.length > 0 || 'Cuisine name cannot be empty';
                  },
                })}
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.region}>
              <FormLabel>Region</FormLabel>
              <Input
                placeholder='Enter region (optional)'
                {...register('region', {
                  maxLength: {
                    value: 255,
                    message: 'Region must be 255 characters or less',
                  },
                })}
              />
              <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.country}>
              <FormLabel>Country</FormLabel>
              <Input
                placeholder='Enter country (optional)'
                {...register('country', {
                  maxLength: {
                    value: 255,
                    message: 'Country must be 255 characters or less',
                  },
                })}
              />
              <FormErrorMessage>{errors.country?.message}</FormErrorMessage>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onCloseModal} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme='blue'
            onClick={handleSubmit(onSubmit)}
            isLoading={isUpdating}
            loadingText='Updating...'
            isDisabled={!isValid}>
            Update Master Cuisine
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
